import { AxiosInstance, AxiosResponse, AxiosStatic } from 'axios';

// import { alert } from 'store/alert';
// import { ServerSideExceptionInfo, BasicObject } from 'types';
import { ThunkDispatch } from 'redux-thunk';
import { AppState } from 'store/types';
import { setConnectionStatus } from 'store/tenant/actions';
import { refreshTenantList } from 'store/tenant/operations';
import { showServerErrorMessage } from 'store/alert/operations';
/* eslint-disable */

const typeJsonRegex = new RegExp('application/json.*');
// ... imports

export default class RestServiceClient {
  restClient: AxiosInstance;
  pollForServerTimeout: number | null;
  dispatch: ThunkDispatch<AppState, any, any> | null;

  constructor(baseURL: string, axios: AxiosStatic) {
    this.restClient = axios.create({
      baseURL,
      validateStatus: () => true,
    });

    this.restClient.interceptors.request.use((config) => {
      const token = localStorage.getItem('token');
      if (token) {
        config.headers['Authorization'] = `Bearer ${token}`;
      }
      return config;
    });

    this.restClient.interceptors.response.use(
      res => {
        // Esto captura respuestas HTTP con error 401 (token inválido)
        if (res.status === 401) {
          console.warn("Token expirado o inválido. Redirigiendo a login.");
          localStorage.removeItem("token");
          window.location.href = "/login"; // o usa react-router navigate si estás en componente
          return Promise.reject(new Error("Unauthorized"));
        }
        return res;
      },
      err => {
        if (err.message === 'Network Error') {
          console.warn('Network Error interceptado por Axios.');
          return Promise.resolve({ data: null, status: 503, headers: {}, config: err.config });
        }

        return Promise.reject(err);
      }
    );

    this.dispatch = null;
    this.pollForServerTimeout = null;
    this.handleResponse = this.handleResponse.bind(this);
  }

  setDispatch(dispatch: ThunkDispatch<AppState, any, any>) {
    this.dispatch = dispatch;
  }

  private handleError(err: { isAxiosError: boolean }): void {
    if (err?.isAxiosError && this.pollForServerTimeout === null) {
      console.error("Axios error detected, retrying...");
      this.pollForServerTimeout = window.setTimeout(() => {
        this.pollForServerTimeout = null;
        if (this.dispatch !== null) {
          this.dispatch(refreshTenantList());
        }
      }, 1000);
    }
  }

  private handleErrorAndReturnNull(err: any): Promise<null> {

    const status = err?.response?.status;

    // Conflicto de asignación en la rotación
    if (status === 409) {

      const responseData = err?.response?.data;

      const message =
        typeof responseData === 'string'
          ? responseData
          : responseData?.message ||
            'El empleado ya se encuentra asignado en otro puesto en este horario.';

      if (this.dispatch !== null) {
        this.dispatch(showServerErrorMessage(message));
      }

      console.warn('[ROTATION CONFLICT]', message);

      return Promise.resolve(null);
    }

    // Mantener el comportamiento actual para los demás errores
    this.handleError(err);

    console.error(
      'Handled error:',
      err?.response?.status,
      err?.response?.data || err?.message
    );

    return Promise.resolve(null);
  }

  private handleResponse<T>(res: AxiosResponse<T>): Promise<T> {

  // Respuestas exitosas
  if (res.status >= 200 && res.status < 300) {
    if (this.dispatch) {
      this.dispatch(setConnectionStatus(true));
    }

    return Promise.resolve(res.data);
  }

  // Conflicto de asignación de empleados
  if (res.status === 409) {

    const responseData: any = res.data;

    const message =
      typeof responseData === 'string'
        ? responseData
        : responseData?.message ||
          'El empleado ya se encuentra asignado en otro puesto en este horario.';

    console.warn('[ROTATION CONFLICT]', message);

    if (this.dispatch !== null) {
      this.dispatch(showServerErrorMessage(message));
    }

    // Mantener el flujo actual:
    // el cliente devuelve null y Redux no actualiza la asignación
    return Promise.reject(new Error('Rotation conflict'));
  }

  // Errores del servidor
  if (
    (res.status >= 502 && res.status <= 504)
    || res.status === 500
  ) {
    this.handleError({ isAxiosError: true });

    return Promise.reject(
      new Error(`Server error: ${res.status}`)
    );
  }

  // No autorizado
  if (res.status === 401) {
    return Promise.reject(new Error('Unauthorized'));
  }

  console.error('Unhandled response:', res.status, res.data);

  return Promise.reject(
    new Error(`Unhandled response status: ${res.status}`)
  );
}

  get<T>(url: string): Promise<T | null> {
    const token = localStorage.getItem('token');
    if (!token || token === "null" || token === "undefined") {
      console.warn("No token found, redirecting to login");
      localStorage.removeItem("token");
      window.location.href = "/login";
      return Promise.reject(new Error("Unauthorized"));
    }
    return this.restClient.get<T>(url)
      .then(this.handleResponse)
      .catch(this.handleErrorAndReturnNull.bind(this));
  }

  post<T>(url: string, params: any): Promise<T | null> {

    const token = localStorage.getItem('token');
    // Permitir llamadas sin token para /user/login
    const isLoginRequest = url === "/user/login";

    if (!token || token === "null" || token === "undefined") {
      console.warn("No token found, redirecting to login");
      localStorage.removeItem("token");
      window.location.href = "/login";
      return Promise.reject(new Error("Unauthorized"));
    }

  
    return this.restClient.post<T>(url, params)
      .then(this.handleResponse)
      .catch(this.handleErrorAndReturnNull.bind(this));
  }

  uploadFile<T>(url: string, file: File): Promise<T> {
    const token = localStorage.getItem('token');
    if (!token || token === "null" || token === "undefined") {
      console.warn("No token found, redirecting to login");
      localStorage.removeItem("token");
      window.location.href = "/login";
      return Promise.reject(new Error("Unauthorized"));
    }

    const data = new FormData();
    data.append('file', file);
    return this.restClient.post<T>(url, data).then(this.handleResponse);
  }

  put<T>(url: string, params: any): Promise<T | null> {
    const token = localStorage.getItem('token');
    if (!token || token === "null" || token === "undefined") {
      console.warn("No token found, redirecting to login");
      localStorage.removeItem("token");
      window.location.href = "/login";
      return Promise.reject(new Error("Unauthorized"));
    }
    return this.restClient.put<T>(url, params)
      .then(this.handleResponse)
      .catch(this.handleErrorAndReturnNull.bind(this));
  }

  delete<T>(url: string): Promise<T | null> {
    const token = localStorage.getItem('token');
    if (!token || token === "null" || token === "undefined") {
      console.warn("No token found, redirecting to login");
      localStorage.removeItem("token");
      window.location.href = "/login";
      return Promise.reject(new Error("Unauthorized"));
    }
    return this.restClient.delete<T>(url)
      .then(this.handleResponse)
      .catch(this.handleErrorAndReturnNull.bind(this));
  }
}
